import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { API_BASE } from '../data/constants.js'
import { useUIStore } from '../store/uiStore.js'

/**
 * useAuth — fetches the current user from the Worker's /whoami endpoint
 * and sets currentUser in the UI store.
 *
 * Production (behind Cloudflare Access):
 *   Worker reads Cf-Access-Authenticated-User-Email and returns { name }.
 *   Component auto-sets currentUser. No manual picker needed.
 *
 * Local dev (localhost, no CF Access):
 *   Endpoint returns { authenticated: false }. We fall back to whatever
 *   name is already in Zustand's persist store (from a previous session)
 *   or leave currentUser as null — developer can use the manual chip.
 *
 * Call this once from App.jsx.
 */
export function useAuth() {
  const currentUser = useUIStore((s) => s.currentUser)
  const setCurrentUser = useUIStore((s) => s.setCurrentUser)

  const { data, isLoading } = useQuery({
    queryKey: ['whoami'],
    queryFn: async () => {
      const res = await fetch(API_BASE + '/whoami')
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
    staleTime: 5 * 60 * 1000, // 5 min — identity rarely changes mid-session
    retry: 0,
  })

  // When the endpoint returns a real name, use it. Override any existing
  // currentUser (handles the case where someone switched logins).
  useEffect(() => {
    if (data?.authenticated && data.name && data.name !== currentUser) {
      setCurrentUser(data.name)
    }
  }, [data, currentUser, setCurrentUser])

  return {
    user: currentUser,
    email: data?.email || null,
    authenticated: Boolean(data?.authenticated),
    isLoading,
    // Dev mode = the endpoint responded but said not authenticated
    isDevMode: data && !data.authenticated,
  }
}
